import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import { Field } from 'formik';
import { useIntl } from 'react-intl';
import { TextField } from '@mui/material';
import { Autocomplete } from 'app/components/FormikField/base';
import type { OwnerDetail, OwnerDetailItem } from 'app/types';
import { EmployerStatus } from 'app/types';

type OwnerRef = { ownerId: string; ownerName: string };

type AutocompleteOption = {
  label: string;
  value: string;
};

type StatusOption = {
  label: string;
  value: EmployerStatus;
};

type FormSelectionState = {
  defaultParentId: string | null;
  owner: OwnerRef;
  subOwners: OwnerDetailItem[];
  defaultSubOwnerId: AutocompleteOption | null;
};

type FormSelectionAction =
  | {
      type: 'SYNC_HIERARCHY';
      owner?: OwnerRef;
      subOwners?: OwnerDetailItem[];
      defaultSubOwnerId: AutocompleteOption | null;
    }
  | { type: 'SELECT_SUB_OWNER'; defaultSubOwnerId: AutocompleteOption | null }
  | { type: 'SET_DEFAULT_PARENT_ID'; payload: string | null };

const formSelectionReducer = (state: FormSelectionState, action: FormSelectionAction): FormSelectionState => {
  switch (action.type) {
    case 'SYNC_HIERARCHY':
      return {
        ...state,
        ...(action.owner !== undefined && { owner: action.owner }),
        ...(action.subOwners !== undefined && { subOwners: action.subOwners }),
        defaultSubOwnerId: action.defaultSubOwnerId,
      };
    case 'SELECT_SUB_OWNER':
      return {
        ...state,
        defaultSubOwnerId: action.defaultSubOwnerId,
        defaultParentId: null,
      };
    case 'SET_DEFAULT_PARENT_ID':
      return { ...state, defaultParentId: action.payload };
    default:
      return state;
  }
};

const toSubOwnerOption = (subOwner: OwnerDetailItem): AutocompleteOption => ({
  label: subOwner.ownerName,
  value: subOwner.ownerId,
});

interface UseEditEmployerFormProps {
  ownerData: OwnerDetail;
  validateForm: () => Promise<any>;
  setFieldValue: (field: string, value: any) => void;
  initialValues: any;
}

interface UseEditEmployerFormReturn {
  isPartOfHoldingChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isPensioenbijIntegrationChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  employerStatusAutoComplete: React.ReactNode;
  parentIdAutoComplete: React.ReactNode;
  subOwnerIdAutoComplete: React.ReactNode;
  ownerAutoComplete: React.ReactNode;
}

const useEditEmployerForm = ({
  ownerData,
  validateForm,
  setFieldValue,
  initialValues,
}: UseEditEmployerFormProps): UseEditEmployerFormReturn => {
  const intl = useIntl();

  const ownerIsSubOwner = !!ownerData.parent;
  const effectiveSubOwnersInit = (ownerIsSubOwner ? ownerData.parent!.subOwners : ownerData.subOwners) ?? [];
  const foundInitialSubOwner = effectiveSubOwnersInit.find(o => o.ownerId === initialValues.ownerId);

  const topOwnerInit = ownerIsSubOwner ? ownerData.parent! : ownerData;

  const initialFormState: FormSelectionState = {
    defaultParentId: initialValues?.parentId ?? null,
    owner: { ownerId: topOwnerInit.ownerId, ownerName: topOwnerInit.ownerName },
    subOwners: effectiveSubOwnersInit,
    defaultSubOwnerId: foundInitialSubOwner ? toSubOwnerOption(foundInitialSubOwner) : null,
  };

  const [state, dispatch] = useReducer(formSelectionReducer, initialFormState);

  const { defaultParentId, owner, subOwners, defaultSubOwnerId } = state;

  useEffect(() => {
    const isSubOwner = !!ownerData.parent;
    const effectiveSubOwners = (isSubOwner ? ownerData.parent!.subOwners : ownerData.subOwners) ?? [];
    const foundSubOwner = effectiveSubOwners.find(o => o.ownerId === initialValues.ownerId);
    const topOwner = isSubOwner ? ownerData.parent! : ownerData;
    dispatch({
      type: 'SYNC_HIERARCHY',
      owner: { ownerId: topOwner.ownerId, ownerName: topOwner.ownerName },
      subOwners: effectiveSubOwners,
      defaultSubOwnerId: foundSubOwner ? toSubOwnerOption(foundSubOwner) : null,
    });
  }, [initialValues.ownerId, ownerData]);

  const isPartOfHoldingChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFieldValue('isPartOfHolding', event.target.checked);
    },
    [setFieldValue],
  );

  const isPensioenbijIntegrationChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFieldValue('isPensioenbijIntegration', event.target.checked);
    },
    [setFieldValue],
  );

  const employerStatusAutoComplete = useMemo(() => {
    const options: StatusOption[] = Object.keys(EmployerStatus).map(status => ({
      label: intl.formatMessage({ id: `employers.${EmployerStatus[status as keyof typeof EmployerStatus]}` }),
      value: EmployerStatus[status as keyof typeof EmployerStatus],
    }));

    options.sort((a, b) => a.label.localeCompare(b.label));

    const initialStatus = options.find(o => o.value === initialValues.status) ?? null;

    return (
      <Field
        name="status"
        component={Autocomplete}
        options={options}
        value={initialStatus}
        onChange={(_: React.SyntheticEvent, value: StatusOption | null) => {
          setFieldValue('status', value?.value ?? initialValues.status);
        }}
        disableClearable={true}
        onBlur={() => validateForm()}
        renderInput={(params: any) => <TextField {...params} name="status" variant="outlined" fullWidth />}
      />
    );
  }, [initialValues.status, intl, setFieldValue, validateForm]);

  const parentIdAutoComplete = useMemo(() => {
    const reducedOptions: AutocompleteOption[] = [];

    const initialParent = reducedOptions.find(o => o.value === defaultParentId) ?? null;

    return (
      <Field
        name="parentId"
        component={Autocomplete}
        options={reducedOptions}
        getOptionLabel={(option: AutocompleteOption) => option.label}
        disableClearable={true}
        value={initialParent}
        isOptionEqualToValue={(item: AutocompleteOption, current: AutocompleteOption | string) => {
          if (typeof current === 'string') return item.value === current;
          return item.value === current.value;
        }}
        onChange={(_: React.SyntheticEvent, value: AutocompleteOption | null) => {
          setFieldValue('parent', value ? { ...value, parentId: value.value } : null);
          setFieldValue('parentId', value?.value ?? '');
          dispatch({ type: 'SET_DEFAULT_PARENT_ID', payload: value?.value ?? null });
        }}
        onBlur={() => validateForm()}
        renderInput={(params: any) => <TextField {...params} name="parentId" variant="outlined" fullWidth />}
      />
    );
  }, [defaultParentId, setFieldValue, validateForm]);

  const subOwnerIdAutoComplete = useMemo(() => {
    const subOwnerOptions: AutocompleteOption[] = subOwners.map(toSubOwnerOption);

    return (
      <Field
        name="subOwnerId"
        component={Autocomplete}
        options={subOwnerOptions}
        getOptionLabel={(option: AutocompleteOption) => option.label}
        disableClearable={false}
        value={defaultSubOwnerId}
        isOptionEqualToValue={(item: AutocompleteOption, current: AutocompleteOption) => item.value === current.value}
        onChange={(_: React.SyntheticEvent, value: AutocompleteOption | null) => {
          setFieldValue('owner', value);
          setFieldValue('subOwnerId', value?.value ?? '');
          setFieldValue('parentId', null);
          dispatch({ type: 'SELECT_SUB_OWNER', defaultSubOwnerId: value });
        }}
        onBlur={() => validateForm()}
        renderInput={(params: any) => <TextField {...params} name="subOwnerId" variant="outlined" fullWidth />}
      />
    );
  }, [defaultSubOwnerId, setFieldValue, subOwners, validateForm]);

  const ownerAutoComplete = useMemo(() => {
    const ownersOptions: AutocompleteOption[] = [{ label: owner.ownerName, value: owner.ownerId }];

    const selectedOwner = ownersOptions.find(o => o.value === owner.ownerId) ?? null;

    return (
      <Field
        name="ownerId"
        component={Autocomplete}
        options={ownersOptions}
        getOptionLabel={(option: AutocompleteOption) => option.label}
        disableClearable={true}
        value={selectedOwner}
        isOptionEqualToValue={(item: AutocompleteOption, current: AutocompleteOption | string) => {
          if (typeof current === 'string') return item.value === current;
          return item.value === current.value;
        }}
        onChange={(_: React.SyntheticEvent, value: AutocompleteOption | null) => {
          setFieldValue('owner', value);
          setFieldValue('ownerId', value?.value ?? '');
        }}
        onBlur={() => validateForm()}
        renderInput={(params: any) => <TextField {...params} name="ownerId" variant="outlined" fullWidth />}
      />
    );
  }, [owner, setFieldValue, validateForm]);

  return {
    isPartOfHoldingChange,
    isPensioenbijIntegrationChange,
    employerStatusAutoComplete,
    parentIdAutoComplete,
    subOwnerIdAutoComplete,
    ownerAutoComplete,
  };
};

export default useEditEmployerForm;
